import { useMemo } from "react";
import { eur, num, fmtDate, fmtDateShort } from "@/lib/format";
import { getPeriodRange, inRange, aggregate } from "@/lib/periods";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Receipt, CalendarDays, Trophy, AlertTriangle, Percent, Wine, Coins } from "lucide-react";

export default function SalesDashboard({ sales, period, custom, expenses = [] }) {
  const range = useMemo(() => getPeriodRange(period, new Date(), custom), [period, custom]);

  const current = useMemo(
    () => sales.filter((s) => inRange(s.date, range.start, range.end)),
    [sales, range]
  );
  const previous = useMemo(
    () => sales.filter((s) => inRange(s.date, range.prevStart, range.prevEnd)),
    [sales, range]
  );

  const agg = useMemo(() => aggregate(current), [current]);
  const prevAgg = useMemo(() => aggregate(previous), [previous]);

  const periodExpenses = useMemo(
    () => expenses.filter((e) => inRange(e.date, range.start, range.end)),
    [expenses, range]
  );
  const totalExpenses = useMemo(() => periodExpenses.reduce((a, b) => a + (b.amount || 0), 0), [periodExpenses]);
  const netCash = agg.total_especes - totalExpenses;

  const delta = prevAgg.total > 0 ? ((agg.total - prevAgg.total) / prevAgg.total) * 100 : null;

  const evolution = useMemo(() => {
    return [...current]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((s) => ({
        date: fmtDateShort(s.date),
        Total: Number(s.total.toFixed(2)),
        "Sur place": Number(s.total_sur_place.toFixed(2)),
        "À emporter": Number(s.total_a_emporter.toFixed(2)),
      }));
  }, [current]);

  const paymentPie = [
    { name: "Carte", value: Number(agg.total_carte.toFixed(2)), color: "#38BDF8" },
    { name: "Espèces", value: Number(agg.total_especes.toFixed(2)), color: "#10B981" },
  ];
  const servicePie = [
    { name: "Sur place", value: Number(agg.total_sur_place.toFixed(2)), color: "#F97316" },
    { name: "À emporter", value: Number(agg.total_a_emporter.toFixed(2)), color: "#818CF8" },
  ];

  if (current.length === 0) {
    return (
      <div className="max-w-6xl mx-auto text-center py-20 vlf-fade-up" data-testid="dashboard-empty">
        <p className="text-[#94A3B8]">Aucune vente sur la période « {range.label} ».</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto vlf-fade-up" data-testid="sales-dashboard">
      <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-1">Tableau de bord</h2>
      <p className="text-sm text-[#94A3B8] mb-5">
        {range.label} — {agg.count} jour{agg.count > 1 ? "s" : ""} de vente
      </p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Kpi testid="kpi-total-ca" title="Chiffre d'affaires TTC" value={eur(agg.total)} icon={Wallet} accent="#FF5A1F" big />
        <Kpi testid="kpi-htva" title="Base HTVA" value={eur(agg.htva_total)} icon={Receipt} accent="#38BDF8" />
        <Kpi testid="kpi-tva-totale" title="TVA due" value={eur(agg.tva_totale)} icon={Percent} accent="#FACC15" />
        <Kpi testid="kpi-daily-avg" title="Moyenne / jour" value={eur(agg.average)} icon={CalendarDays} accent="#10B981" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div data-testid="kpi-period-comparison" className="rounded-xl bg-[#131920] border border-[#253242] p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8] mb-1">vs période préc.</div>
          {delta === null ? (
            <div className="font-mono text-lg text-[#64748B]">—</div>
          ) : (
            <div className={`flex items-center gap-1.5 font-mono text-xl font-black ${delta >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
              {delta >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              {delta >= 0 ? "+" : ""}{num(delta)} %
            </div>
          )}
          <div className="text-xs text-[#64748B] mt-1 font-mono">Préc. : {eur(prevAgg.total)}</div>
        </div>

        <Kpi testid="kpi-tva-12" title="TVA 12% (sur place)" value={eur(agg.tva_12)} icon={Percent} accent="#F97316" />
        <div data-testid="kpi-best-day" className="rounded-xl bg-[#131920] border border-[#253242] p-4">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#10B981] mb-1">
            <Trophy className="w-3.5 h-3.5" /> Meilleur jour
          </div>
          <div className="font-mono text-lg font-black text-white">{agg.best ? eur(agg.best.total) : "—"}</div>
          {agg.best && <div className="text-xs text-[#64748B] mt-0.5">{fmtDate(agg.best.date)}</div>}
        </div>
        <div data-testid="kpi-worst-day" className="rounded-xl bg-[#131920] border border-[#253242] p-4">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#EF4444] mb-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Jour le plus faible
          </div>
          <div className="font-mono text-lg font-black text-white">{agg.worst ? eur(agg.worst.total) : "—"}</div>
          {agg.worst && <div className="text-xs text-[#64748B] mt-0.5">{fmtDate(agg.worst.date)}</div>}
        </div>
      </div>

      {/* VAT + cash row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi testid="kpi-boissons" title="Boissons sur place (suivi)" value={eur(agg.boissons_sur_place)} icon={Wine} accent="#F472B6" />
        <Kpi testid="kpi-tva-6" title="TVA 6% (à emporter)" value={eur(agg.tva_6)} icon={Percent} accent="#818CF8" />
        <Kpi testid="kpi-depenses-cash" title="Dépenses cash" value={eur(totalExpenses)} icon={Wallet} accent="#EF4444" />
        <Kpi testid="kpi-net-cash" title="Cash net (espèces − dépenses)" value={eur(netCash)} icon={Coins} accent={netCash >= 0 ? "#10B981" : "#EF4444"} />
      </div>

      {/* Evolution chart */}
      <div data-testid="chart-ca-evolution" className="rounded-2xl bg-[#131920] border border-[#253242] p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">Évolution du chiffre d'affaires</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={evolution} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#253242" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#253242" }} />
            <YAxis tick={{ fill: "#64748B", fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: "#1A222C", border: "1px solid #3A4D66", borderRadius: 12, color: "#fff" }}
              formatter={(v) => eur(v)}
              cursor={{ fill: "rgba(255,90,31,0.08)" }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Sur place" stackId="a" fill="#F97316" radius={[0, 0, 0, 0]} />
            <Bar dataKey="À emporter" stackId="a" fill="#818CF8" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="Total" stroke="#FF5A1F" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Pie charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PieCard testid="chart-payment-pie" title="Carte vs Espèces" data={paymentPie} />
        <PieCard testid="chart-service-pie" title="Sur place vs À emporter" data={servicePie} />
      </div>
    </div>
  );
}

function Kpi({ title, value, icon: Icon, accent, testid, big }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-[#131920] border border-[#253242] p-4">
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className={`font-mono font-black text-white ${big ? "text-2xl sm:text-3xl" : "text-xl"}`}>{value}</div>
    </div>
  );
}

function PieCard({ title, data, testid }) {
  const total = data.reduce((a, b) => a + b.value, 0);
  return (
    <div data-testid={testid} className="rounded-2xl bg-[#131920] border border-[#253242] p-4">
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
            {data.map((d) => (
              <Cell key={d.name} fill={d.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ background: "#1A222C", border: "1px solid #3A4D66", borderRadius: 12, color: "#fff" }}
            formatter={(v) => `${eur(v)} (${total ? Math.round((v / total) * 100) : 0}%)`}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
